import React, { useEffect, useMemo, useState, useContext } from 'react';

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Modal from 'react-bootstrap/Modal';
import 'bootstrap/dist/css/bootstrap.min.css';

import classNames from 'classnames';
import FormDropdown from '../../components/Form/FormDropdown';
import Textarea from '../../components/Form/Textarea';
import Switch from '../../components/GradientSwitch';
import RadioButton from '../../components/Form/RadioButton';
import Button from '../../components/Button';
import FilterSwitch from './FilterSwitch';
import Badge from './Badge';

import { UiContext } from '../../context/UiContext';
import AdminService from '../../services/AdminService';
import classes from './styles.module.scss';
import { useUserStore } from '../../store';
// Submission modes
const MODES = {
  INTERNAL: 'Internal',
  CLIENT: 'Client',
};

export default function SubmitCandidateModal({
  show,
  handleClose,
  currentCandidate,
  refetch,
  jobPostId,
  submissionFlow,
}) {
  console.log('currentCandidate', currentCandidate);
  console.log('jobPostId', jobPostId);
  console.log('submissionFlow', submissionFlow);
  const { t } = useTranslation();
  const { setIsFetching, showNotification, showModal } = useContext(UiContext);
  const isScreeningAvailable = useUserStore(
    (state) => state.isScreeningAvailable
  );
  // UI state
  const [mode, setMode] = useState(
    submissionFlow === 'client' ? MODES.CLIENT : MODES.INTERNAL
  );
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [primaryContact, setPrimaryContact] = useState('');
  const [notifyAll, setNotifyAll] = useState(false);
  const [workedBefore, setWorkedBefore] = useState(null); // null | true | false
  const [hasConsent, setHasConsent] = useState(null); // null | true | false
  const [coverNote, setCoverNote] = useState('');
  const [touched, setTouched] = useState(false);
  const [areBadgesEnabled, setAreBadgesEnabled] = useState(false);
  const [enabledBadges, setEnabledBadges] = useState([]);
  const [isPrioritized, setIsPrioritized] = useState(false);

  // Check if there are any jobs available for this candidate
  const { data: availableJobsData, isLoading: isLoadingAvailableJobs } =
    useQuery({
      queryKey: [
        'jobposts-picker-available',
        currentCandidate?.user?.id || currentCandidate?.id,
      ],
      queryFn: () =>
        AdminService.getJobPostsPicker({
          pageNumber: 1,
          pageSize: 1,
          UserId: currentCandidate?.user?.id || currentCandidate?.id,
        }),
      enabled: show && !jobPostId,
      staleTime: 60 * 1000,
    });

  const hasAvailableJobs = useMemo(() => {
    if (jobPostId) return true; // If jobPostId is provided, we assume there's a job
    if (isLoadingAvailableJobs) return true; // Show dropdown while loading
    return availableJobsData?.data && availableJobsData.data.length > 0;
  }, [jobPostId, isLoadingAvailableJobs, availableJobsData]);

  const { data: tags } = useQuery({
    queryKey: ['tags'],
    queryFn: () =>
      AdminService.getCandidateTags({
        pageSize: 50,
      }),
    enabled: show,
    staleTime: 60 * 1000,
  });

  const stageOptions = useMemo(
    () => [
      {
        label: t('modals.SubmitCandidateModal.stages.new'),
        value: 'New',
        disabled: false,
      },
      {
        label: t('modals.SubmitCandidateModal.stages.screen'),
        value: 'Screen',
        disabled: !isScreeningAvailable,
      },
      {
        label: t('modals.SubmitCandidateModal.stages.interview'),
        value: 'Interview',
        disabled: false,
      },
      {
        label: t('modals.SubmitCandidateModal.stages.offer'),
        value: 'Offer',
        disabled: false,
      },
    ],
    [t, isScreeningAvailable]
  );

  const [contactOptions, setContactOptions] = useState([]);

  // Preselect job when jobPostId is provided and hide picker
  useEffect(() => {
    if (show && jobPostId) {
      setSelectedJob(jobPostId);
    }
    setEnabledBadges([]);
    setAreBadgesEnabled(false);
    setIsPrioritized(false);
  }, [show, jobPostId]);

  // Force client mode when submissionFlow is 'client'
  useEffect(() => {
    if (show && submissionFlow === 'client' && mode !== MODES.CLIENT) {
      setMode(MODES.CLIENT);
    }
  }, [show, submissionFlow, mode]);

  // Refetch picker for a specific job id when selection changes to populate contacts
  useEffect(() => {
    // Only run when modal is shown and in client mode
    if (!show || mode !== MODES.CLIENT) {
      if (mode === MODES.CLIENT && !show) {
        setContactOptions([]);
        setPrimaryContact(null);
      }
      return;
    }

    const jobId = selectedJob?.value || selectedJob || jobPostId;

    if (!jobId) {
      setContactOptions([]);
      setPrimaryContact(null);
      return;
    }

    // Clear options immediately when job changes
    setContactOptions([]);
    setPrimaryContact(null);

    const loadContacts = async () => {
      setIsFetching(true);
      try {
        const page = await AdminService.getJobPostsPicker({
          pageNumber: 1,
          pageSize: 1,
          Filters: `id:eq:${jobId}`,
        });
        const job = page?.data?.[0];
        const managers = job?.company?.managers || job?.contacts || [];
        let options = [];
        if (managers.length > 0) {
          options = managers
            .filter((m) => m.userId !== null && m.userId !== undefined)
            .map((m) => ({
              label: `${m.firstName} ${m.lastName} ${
                m.designation ? `(${m.designation})` : ''
              }`.trim(),
              value: m.id,
            }));
        }
        setContactOptions(options);
        const defaultManagerId = job?.companyManager?.id;
        if (defaultManagerId) {
          const defaultManager = options.find(
            (o) => o.value === defaultManagerId
          );
          if (defaultManager) {
            setPrimaryContact(defaultManager);
          } else {
            setPrimaryContact(null);
          }
        } else {
          setPrimaryContact(null);
        }
        setIsFetching(false);
      } catch (e) {
        setIsFetching(false);
        setContactOptions([]);
        setPrimaryContact(null);
      }
    };

    loadContacts();
  }, [show, selectedJob, mode, jobPostId]);

  const resetForm = () => {
    setMode(submissionFlow === 'client' ? MODES.CLIENT : MODES.INTERNAL);
    setSelectedJob(null);
    setSelectedStage(null);
    setPrimaryContact(null);
    setNotifyAll(false);
    setWorkedBefore(null);
    setHasConsent(null);
    setCoverNote('');
    setTouched(false);
    setContactOptions([]);
  };

  useEffect(() => {
    if (!show) {
      setMode(submissionFlow === 'client' ? MODES.CLIENT : MODES.INTERNAL);
      setSelectedJob(null);
      setSelectedStage(null);
      setPrimaryContact(null);
      setNotifyAll(false);
      setWorkedBefore(null);
      setHasConsent(null);
      setCoverNote('');
      setTouched(false);
      setContactOptions([]);
    }
  }, [show, submissionFlow]);

  useEffect(() => {
    if (!jobPostId) {
      setSelectedJob(null);
    }
    setSelectedStage(null);
    setPrimaryContact(null);
    setNotifyAll(false);
    setWorkedBefore(null);
    setHasConsent(null);
    setCoverNote('');
    setTouched(false);
  }, [mode, jobPostId]);

  const isInternalValid =
    mode === MODES.INTERNAL && selectedJob && selectedStage;

  const isClientValid =
    mode === MODES.CLIENT &&
    selectedJob &&
    primaryContact &&
    coverNote !== null &&
    coverNote !== '' &&
    workedBefore !== null &&
    hasConsent !== null;

  const isSubmitEnabled = isInternalValid || isClientValid;

  const onSubmit = async () => {
    setTouched(true);
    if (!isSubmitEnabled) return;
    try {
      setIsFetching(true);
      const payload = {
        userId: currentCandidate.user?.id || currentCandidate?.id,
        submitType: mode,
        jobApplicationTagIds: areBadgesEnabled
          ? enabledBadges?.map((tag) => tag.id)
          : [],
        jobPostId: jobPostId || selectedJob?.value || selectedJob,
        applicationStatus:
          mode === MODES.INTERNAL
            ? selectedStage?.value || selectedStage
            : 'ClientSubmit',
        companyManagerId:
          mode === MODES.CLIENT
            ? primaryContact?.value || primaryContact
            : null,
        notify: mode === MODES.CLIENT ? notifyAll : false,
        previousWork: mode === MODES.CLIENT ? workedBefore : false,
        consent: mode === MODES.CLIENT ? hasConsent : false,
        coverMessage: mode === MODES.CLIENT ? coverNote : '',
        isPrioritized,
      };

      await AdminService.submitCandidate(payload);

      if (refetch) {
        await refetch();
      }
      showNotification({
        text: t('modals.SubmitCandidateModal.toast.submitted'),
      });
      handleClose();
    } catch (error) {
      console.log('error', error);
      const isDuplicity = error?.response?.status === 409;
      showModal({
        title: isDuplicity
          ? t('modals.SubmitCandidateModal.duplicityTitle')
          : t('common.error'),
        text: isDuplicity
          ? t('modals.SubmitCandidateModal.duplicityText')
          : t('common.somethingWentWrong'),
        onCancel: () => {
          resetForm();
        },
      });
    } finally {
      setIsFetching(false);
    }
  };

  const filterSwitchOptions = useMemo(
    () => [
      {
        label: t('modals.SubmitCandidateModal.pill.internal'),
        value: MODES.INTERNAL,
      },
      {
        label: t('modals.SubmitCandidateModal.pill.client'),
        value: MODES.CLIENT,
      },
    ],
    [t]
  );

  const activeFilterSwitchOption = useMemo(
    () => filterSwitchOptions.find((opt) => opt.value === mode),
    [filterSwitchOptions, mode]
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      enforceFocus={false}
      className={classes.SubmitCandidateModal}
      backdropClassName={classes.backdrop}
      contentClassName={classes.modalContent}
      dialogClassName={classes.dialog}
    >
      <div className={classes.content}>
        <header className={classes.header}>
          <h2>{t('modals.SubmitCandidateModal.title')}</h2>
          <div className={classes.headerRight}>
            {submissionFlow !== 'client' && (
              <FilterSwitch
                activeFilterSwitchOption={activeFilterSwitchOption}
                setActiveFilterSwitchOption={(option) => setMode(option.value)}
                filterSwitchOptions={filterSwitchOptions}
              />
            )}
            <button
              type="button"
              className={classes.closeButton}
              onClick={handleClose}
            />
          </div>
        </header>

        <div className={classes.form}>
          <div className={classes.row}>
            {!jobPostId && mode !== MODES.CLIENT && (
              <FormDropdown
                label={t('modals.SubmitCandidateModal.job.label')}
                width={mode === MODES.INTERNAL ? 359 : 735}
                value={selectedJob}
                note={
                  hasAvailableJobs
                    ? ''
                    : t('modals.SubmitCandidateModal.job.noJobsAvailable')
                }
                placeholder={t('modals.SubmitCandidateModal.job.placeholder')}
                height={45}
                setFieldValue={(_name, option) => setSelectedJob(option)}
                touched={touched}
                error={touched && !selectedJob ? t('common.required') : null}
                fetchOptions={{
                  isJobPostsPicker: true,
                  dataName: 'submitModalJobsPicker',
                  getDataHandler: ({ pageNumber, term }) =>
                    AdminService.getJobPostsPicker({
                      pageNumber,
                      term,
                      UserId: currentCandidate.user?.id || currentCandidate?.id,
                    }),
                }}
              />
            )}

            {!jobPostId && mode === MODES.CLIENT && (
              <FormDropdown
                label={t('modals.SubmitCandidateModal.job.label')}
                width={mode === MODES.INTERNAL ? 359 : 735}
                value={selectedJob}
                note={
                  hasAvailableJobs
                    ? ''
                    : t('modals.SubmitCandidateModal.job.noJobsAvailable')
                }
                placeholder={t('modals.SubmitCandidateModal.job.placeholder')}
                height={45}
                setFieldValue={(_name, option) => setSelectedJob(option)}
                touched={touched}
                error={touched && !selectedJob ? t('common.required') : null}
                fetchOptions={{
                  isJobPostsPicker: true,
                  dataName: 'submitModalJobsPicker',
                  getDataHandler: ({ pageNumber, term }) =>
                    AdminService.getJobPostsPicker({
                      pageNumber,
                      term,
                      UserId: currentCandidate.user?.id || currentCandidate?.id,
                      Filters: `jobPostType:eq:External`,
                    }),
                }}
              />
            )}

            {mode === MODES.INTERNAL && (
              <FormDropdown
                label={t('modals.SubmitCandidateModal.stage.label')}
                options={stageOptions.filter((stage) => !stage.disabled)}
                width={359}
                value={selectedStage}
                placeholder={t('modals.SubmitCandidateModal.stage.placeholder')}
                height={45}
                setFieldValue={(_name, option) => setSelectedStage(option)}
                touched={touched}
                error={touched && !selectedStage ? t('common.required') : null}
              />
            )}
          </div>

          {mode === MODES.CLIENT && selectedJob && (
            <>
              <div className={classes.row}>
                <div className={classes.badgesContainer}>
                  <Switch
                    label={t(
                      'dashboardComponents.Companies.CompanyDetails.Settings.Badges.enableBadges'
                    )}
                    isEnabled={areBadgesEnabled}
                    onClick={() => {
                      if (areBadgesEnabled) {
                        setAreBadgesEnabled(false);
                        setEnabledBadges([]);

                        return;
                      }
                      setAreBadgesEnabled((prevState) => !prevState);
                    }}
                  />
                  <div
                    className={classNames(classes.badges, {
                      [classes.active]: areBadgesEnabled,
                    })}
                  >
                    {tags?.data?.map?.((tag) => (
                      <Badge
                        key={tag.id}
                        badge={tag}
                        areBadgesEnabled={areBadgesEnabled}
                        enabledBadges={enabledBadges}
                        setEnabledBadges={setEnabledBadges}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className={classes.row}>
                <div className={classes.badgesContainer}>
                  <Switch
                    label={t('common.priority')}
                    isEnabled={isPrioritized}
                    onClick={() => {
                      setIsPrioritized((prevState) => !prevState);
                    }}
                  />
                </div>
              </div>
              <div className={classNames(classes.row, classes.rowClient)}>
                <FormDropdown
                  label={t('modals.SubmitCandidateModal.primaryContact.label')}
                  options={contactOptions}
                  width={600}
                  value={primaryContact}
                  isExternalUserList
                  placeholder={t(
                    'modals.SubmitCandidateModal.primaryContact.placeholder'
                  )}
                  height={45}
                  setFieldValue={(_name, option) => setPrimaryContact(option)}
                  touched={touched}
                  error={
                    touched && !primaryContact ? t('common.required') : null
                  }
                />
                <div className={classes.notifyContainer}>
                  <span>{t('modals.SubmitCandidateModal.notifyAll')}</span>
                  <Switch
                    isEnabled={notifyAll}
                    onClick={() => setNotifyAll(!notifyAll)}
                  />
                </div>
              </div>

              <div className={classes.rowRadios}>
                <div className={classes.radioGroup}>
                  <span>{t('modals.SubmitCandidateModal.workedBefore')}</span>
                  <div className={classes.radios}>
                    <RadioButton
                      label={t('common.yes')}
                      isSelected={workedBefore === true}
                      onClick={() => setWorkedBefore(true)}
                    />
                    <RadioButton
                      label={t('common.no')}
                      isSelected={workedBefore === false}
                      onClick={() => setWorkedBefore(false)}
                    />
                  </div>
                </div>
                <div className={classes.radioGroup}>
                  <span>
                    {t('modals.SubmitCandidateModal.candidateConsent')}
                  </span>
                  <div className={classes.radios}>
                    <RadioButton
                      label={t('common.yes')}
                      isSelected={hasConsent === true}
                      onClick={() => setHasConsent(true)}
                    />
                    <RadioButton
                      label={t('common.no')}
                      isSelected={hasConsent === false}
                      onClick={() => setHasConsent(false)}
                    />
                  </div>
                </div>
              </div>

              <div className={classes.row}>
                <div className={classes.textareaContainerFull}>
                  <Textarea
                    label={t('modals.SubmitCandidateModal.coverNote.label')}
                    note={t('modals.SubmitCandidateModal.coverNote.note')}
                    width={735}
                    height={120}
                    placeholder={t(
                      'modals.SubmitCandidateModal.coverNote.placeholder'
                    )}
                    maxLength={5000}
                    value={coverNote}
                    onChange={(e) => setCoverNote(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className={classes.buttons}>
          <Button
            width={250}
            height={52}
            variant="blackWhite"
            onClick={handleClose}
          >
            {t('common.cancel')}
          </Button>
          <Button
            width={250}
            height={52}
            disabled={!isSubmitEnabled}
            onClick={onSubmit}
          >
            {t('common.submit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
